import { useEffect, useRef, useState } from 'react';
import { Fade } from 'react-awesome-reveal';

/**
 * InViewFade: SSR-safe wrapper that only mounts the Fade animation on client-side.
 * It renders children without animation during SSR and triggers Fade once the element enters the viewport.
 */
const InViewFade = ({ children, fraction = 0.3, rootMargin = '0px', placeholderStyle, ...fadeProps }) => {
    const isClient = typeof window !== 'undefined';
    const [visible, setVisible] = useState(!isClient);
    const nodeRef = useRef(null);

    useEffect(() => {
        if (!isClient || visible) return undefined;

        const node = nodeRef.current;
        if (!node) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio >= fraction) {
                        setVisible(true);
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: fraction, rootMargin }
        );

        observer.observe(node);

        return () => observer.disconnect();
    }, [fraction, rootMargin, isClient, visible]);

    // Use consistent structure for SSR hydration: server and client initial render both use the div wrapper
    const hiddenWrapper = (
        <div ref={nodeRef} style={{ visibility: 'hidden', ...(placeholderStyle || {}) }}>
            {children}
        </div>
    );

    if (!isClient) return hiddenWrapper;

    if (visible) return <Fade {...fadeProps}>{children}</Fade>;

    return hiddenWrapper;
};

export default InViewFade;
